import React, { useEffect, useRef, useState } from 'react';
import { TabOption } from './utils';
import { Animated, Easing, Platform, View } from 'react-native';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import { useCurrentAndPreviousTab } from './hooks';
import { useForceUpdate } from '../../../hooks/useForceUpdate';
import { ANIMATION_DURATION } from './constants';
import { PRScrollView } from '../../shared/Scroll';

export type TabContentContainerOwnProps = {
    tabs: TabOption[];
    selectedTab: string;
    scrollable: boolean;
    tabContainerWidth: number;
    onlyRenderActiveTab?: boolean;
};

export type TabContentContainerProps = TabContentContainerOwnProps;

const getScreenTransitionInterpolation = (
    tabs: TabOption[],
    previousTab: string,
    selectedTab: string,
    tabContainerWidth: number,
) => {
    const isSameTab = previousTab === selectedTab;
    const previousTabIndex = tabs.findIndex((tab) => tab.value === previousTab);
    const selectedTabIndex = tabs.findIndex((tab) => tab.value === selectedTab);
    const isMovingRight = previousTabIndex < selectedTabIndex;
    return {
        exitingInterpolatedValues: [0, isMovingRight ? -tabContainerWidth : tabContainerWidth],
        enteringInterpolatedValues: [isSameTab ? 0 : isMovingRight ? tabContainerWidth : -tabContainerWidth, 0],
    };
};

const TabContentContainer: React.FC<TabContentContainerProps> = (props) => {
    const { forceUpdate } = useForceUpdate();
    const screenTransitionAnimationRef = useRef<Animated.Value>(new Animated.Value(0));
    const exitTransitionCompletedRef = useRef(true);
    const animationRunIdRef = useRef(0);
    const { selectedTab, previousTab } = useCurrentAndPreviousTab({
        selectedTab: props.selectedTab,
        onTabChange: () => {
            exitTransitionCompletedRef.current = false;
            screenTransitionAnimationRef.current.setValue(0);
        },
    });
    const isMobile = useIsMobileLayout();
    const [allShownTabs, setAllShowTabs] = useState<string[]>([selectedTab]);
    useEffect(() => {
        const newTabs = [...new Set([...allShownTabs, selectedTab])];
        if (newTabs.length !== allShownTabs.length) {
            setAllShowTabs(newTabs);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTab]);
    const screenTransitionInterpolation = getScreenTransitionInterpolation(
        props.tabs,
        previousTab || props.selectedTab,
        props.selectedTab,
        props.tabContainerWidth || 0,
    );
    useEffect(() => {
        const runId = ++animationRunIdRef.current;
        screenTransitionAnimationRef.current.setValue(0);
        Animated.sequence([
            Animated.timing(screenTransitionAnimationRef.current, {
                toValue: 1,
                duration: props.onlyRenderActiveTab ? 0 : ANIMATION_DURATION,
                useNativeDriver: Platform.OS === 'web',
                easing: Easing.inOut(Easing.ease),
            }),
        ]).start(() => {
            // Mark the transition complete whether it finished or was interrupted. setValue(0) on a
            // rapid tab change interrupts the previous run, which reports finished: false — leaving
            // the ref false and this subtree tagged `animating-parent` indefinitely. The run id
            // keeps a superseded run from clearing the flag its replacement still needs.
            if (animationRunIdRef.current !== runId) return;
            exitTransitionCompletedRef.current = true;
            forceUpdate();
        });
        forceUpdate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTab]);

    const maybeRenderInScrollable = (content: React.ReactNode) => {
        if (props.scrollable) {
            return (
                <PRScrollView style={{ width: '100%', flex: 1 }} contentContainerStyle={{ flexGrow: 1, width: '100%' }}>
                    {content}
                </PRScrollView>
            );
        } else {
            return <View style={{ width: '100%', flex: 1 }}>{content}</View>;
        }
    };

    if (props.onlyRenderActiveTab) {
        const selectedTabConfig = props.tabs.find((tab) => tab.value === selectedTab);
        if (!selectedTabConfig) return null;

        return (
            <Animated.View
                style={{
                    position: 'absolute',
                    top: 0,
                    left: isMobile
                        ? screenTransitionAnimationRef.current.interpolate({
                              inputRange: [0, 1],
                              outputRange: screenTransitionInterpolation.enteringInterpolatedValues,
                          })
                        : 0,
                    opacity: isMobile
                        ? 1
                        : screenTransitionAnimationRef.current.interpolate({
                              inputRange: [0, 1],
                              outputRange: [0, 1],
                          }),
                    width: '100%',
                    height: '100%',
                    zIndex: 1,
                }}
                testID={!exitTransitionCompletedRef.current ? 'animating-parent' : 'side-tab-content-container'}
            >
                {maybeRenderInScrollable(selectedTabConfig.renderTabContent())}
            </Animated.View>
        );
    } else {
        return (
            <View style={{ flex: 1, width: '100%', position: 'relative' }} testID="tab-content-container">
                {[...(props.tabs || [])]
                    .sort((t1, t2) => {
                        /*
                            Sort tabs so that the selected tab is rendered last.

                            This theoretically doesn't matter for the user, but cypress may consider an element covered by another element
                        */
                        if (t1.value === selectedTab) return 1;

                        if (t2.value === selectedTab) return -1;

                        return 0;
                    })
                    .map<React.ReactNode>((tab) => {
                        /**
                         * This is tricky... but keep tabs mounted after they've already rendered once
                         * If you mount and unmount or even swap out the content of the entering/present and exiting view,
                         * that will trigger a remount because the parent changes
                         */
                        if (!allShownTabs.includes(tab.value)) return null;

                        const curTab = props.tabs.find((_) => _.value === tab.value)!;
                        if (
                            tab.value === previousTab &&
                            !exitTransitionCompletedRef.current &&
                            !props.onlyRenderActiveTab
                        ) {
                            return (
                                <Animated.View
                                    key={tab.value}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: isMobile
                                            ? screenTransitionAnimationRef.current.interpolate({
                                                  inputRange: [0, 1],
                                                  outputRange: screenTransitionInterpolation.exitingInterpolatedValues,
                                              })
                                            : 0,
                                        opacity: isMobile
                                            ? 1
                                            : screenTransitionAnimationRef.current.interpolate({
                                                  inputRange: [0, 1],
                                                  outputRange: [1, 0],
                                              }),
                                        width: '100%',
                                        height: '100%',
                                        zIndex: 1,
                                    }}
                                    testID="animating-parent"
                                >
                                    {maybeRenderInScrollable(curTab.renderTabContent())}
                                </Animated.View>
                            );
                        } else if (tab.value === selectedTab) {
                            return (
                                <Animated.View
                                    key={tab.value}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: isMobile
                                            ? screenTransitionAnimationRef.current.interpolate({
                                                  inputRange: [0, 1],
                                                  outputRange: screenTransitionInterpolation.enteringInterpolatedValues,
                                              })
                                            : 0,
                                        opacity: isMobile
                                            ? 1
                                            : screenTransitionAnimationRef.current.interpolate({
                                                  inputRange: [0, 1],
                                                  outputRange: [0, 1],
                                              }),
                                        width: '100%',
                                        height: '100%',
                                        zIndex: 1,
                                    }}
                                    testID={!exitTransitionCompletedRef.current ? 'animating-parent' : undefined}
                                >
                                    {maybeRenderInScrollable(curTab.renderTabContent())}
                                </Animated.View>
                            );
                        } else if (!props.onlyRenderActiveTab) {
                            return (
                                <Animated.View
                                    key={tab.value}
                                    style={{
                                        display: 'none',
                                        zIndex: -1,
                                    }}
                                    testID="animating-parent"
                                >
                                    {maybeRenderInScrollable(curTab.renderTabContent())}
                                </Animated.View>
                            );
                        } else {
                            return null;
                        }
                    })}
            </View>
        );
    }
};

export default React.memo(TabContentContainer);
